import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Mail, ArrowRight, Building2, Send } from "lucide-react";
import { SiteFooter } from "@/components/SiteFooter";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
const panel = "rounded-2xl border border-white/5 bg-gradient-to-b from-[hsl(222,42%,15%)] to-[hsl(222,46%,10%)] shadow-xl shadow-black/30 ring-1 ring-white/5";

const TITLE = "Contact — reach the Send-Proof team | Send-Proof";
const DESC = "Contact Send-Proof: email support@send-proof.com or use the form. Send-Proof is built and operated by Arlo Douglas Labs LLC, founded by Ryan Trexler.";

export default function Contact() {
  const navigate = useNavigate();
  const go = () => navigate("/auth?mode=signup");
  const [name, setName] = useState("");
  const [from, setFrom] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", DESC);
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
    };
  }, []);

  const submit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`Send-Proof — message from ${name || "the website"}`);
    const body = encodeURIComponent(`${message}\n\n— ${name}${from ? ` (${from})` : ""}`);
    window.location.href = `mailto:support@send-proof.com?subject=${subject}&body=${body}`;
  };

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-card text-foreground">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[700px]">
        <div className="absolute left-1/2 top-[-200px] h-[460px] w-[800px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[130px]" />
      </div>
      <div className="relative z-10">
        <header className="border-b border-white/10 bg-[hsl(222,46%,10%)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="contact-logo">
            <img src="/logo-mark.webp" alt="Send-Proof" className="h-8 w-8 rounded-lg" />
            <span className="font-heading text-lg font-bold tracking-tight">Send-Proof</span>
          </Link>
          <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="contact-nav-cta" onClick={go}>Start free trial</Button>
          </div>
        </header>

        <motion.section variants={reveal} initial="hidden" animate="show" className="mx-auto max-w-2xl px-5 pt-10 text-center sm:px-8 sm:pt-16" data-testid="contact-page">
          <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">Contact Send-Proof</h1>
          <p className="mx-auto mt-4 max-w-lg text-base text-muted-foreground sm:text-lg">
            Send-Proof is built and operated by Arlo Douglas Labs LLC, founded by Ryan Trexler. Email us directly or use the form — a real person reads every message.
          </p>
        </motion.section>

        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mx-auto grid max-w-3xl gap-5 px-5 pt-10 sm:grid-cols-2 sm:px-8">
          {/* Direct email */}
          <div className={`${panel} p-7`} data-testid="contact-email-card">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500 text-white"><Mail className="h-6 w-6" /></div>
            <h2 className="mt-4 font-heading text-lg font-bold">Email us</h2>
            <p className="mt-1 text-sm text-muted-foreground">For support, billing, or anything else. We reply within one business day.</p>
            <a href="mailto:support@send-proof.com" className="mt-4 inline-flex items-center gap-2 font-semibold text-emerald-400 hover:text-emerald-300" data-testid="contact-email-link">
              support@send-proof.com <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          {/* Company */}
          <div className={`${panel} p-7`} data-testid="contact-company-card">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500/15 text-emerald-400"><Building2 className="h-6 w-6" /></div>
            <h2 className="mt-4 font-heading text-lg font-bold">Who runs Send-Proof</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Arlo Douglas Labs LLC — a small independent team led by founder and working contractor Ryan Trexler.
            </p>
            <Link to="/about" className="mt-4 inline-flex items-center gap-2 font-semibold text-emerald-400 hover:text-emerald-300" data-testid="contact-about-link">
              Read our story <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.section>

        {/* Contact form (opens your mail client — no data leaves your device until you hit send) */}
        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mx-auto max-w-2xl px-5 pt-10 pb-24 sm:px-8">
          <form onSubmit={submit} className={`${panel} space-y-4 p-7`} data-testid="contact-form">
            <h2 className="font-heading text-lg font-bold">Send a message</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-sm font-medium" htmlFor="contact-name">Your name</label>
                <input id="contact-name" data-testid="contact-name-input" value={name} onChange={(e) => setName(e.target.value)} required
                  className="mt-1 h-11 w-full rounded-md border border-border bg-[hsl(222,44%,12%)] px-3 text-sm outline-none focus:border-emerald-500" />
              </div>
              <div>
                <label className="text-sm font-medium" htmlFor="contact-from">Your email</label>
                <input id="contact-from" data-testid="contact-from-input" type="email" value={from} onChange={(e) => setFrom(e.target.value)} required
                  className="mt-1 h-11 w-full rounded-md border border-border bg-[hsl(222,44%,12%)] px-3 text-sm outline-none focus:border-emerald-500" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium" htmlFor="contact-message">Message</label>
              <textarea id="contact-message" data-testid="contact-message-input" value={message} onChange={(e) => setMessage(e.target.value)} required rows={5}
                className="mt-1 w-full rounded-md border border-border bg-[hsl(222,44%,12%)] px-3 py-2 text-sm outline-none focus:border-emerald-500" />
            </div>
            <Button type="submit" size="lg" className="h-12 w-full bg-emerald-500 text-base hover:bg-emerald-600" data-testid="contact-submit">
              <Send className="mr-1 h-4 w-4" /> Send message
            </Button>
            <p className="text-center text-xs text-muted-foreground">This opens your email app addressed to support@send-proof.com.</p>
          </form>
        </motion.section>

        <SiteFooter />
      </div>
    </div>
  );
}
